import CloseIcon from "@mui/icons-material/Close";
import {
    Alert,
    Box,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import React from "react";
import { useGetApiLogDetailQuery } from "../../../redux/api";
import { describeClient } from "../../../utils/common";
import { StatusChip, formatDateTime, formatMs, routeLabel } from "../securityUtils";

const Field = ({ label, value, mono = false }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
            {label}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                fontFamily: mono ? "monospace" : undefined,
                wordBreak: "break-all",
                fontWeight: 500,
            }}
        >
            {value === null || value === undefined || value === "" ? "-" : value}
        </Typography>
    </Box>
);

// `requestBody`/`queryParams` come back as parsed JSON from the JSON column, but
// tolerate a string in case the driver hands one back.
const prettyJson = (value) => {
    if (value === null || value === undefined) return null;
    try {
        const parsed = typeof value === "string" ? JSON.parse(value) : value;
        if (parsed && typeof parsed === "object" && Object.keys(parsed).length === 0) return null;
        return JSON.stringify(parsed, null, 2);
    } catch (e) {
        return String(value);
    }
};

const JsonBlock = ({ title, value }) => {
    const text = prettyJson(value);
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
                {title}
            </Typography>
            <Box
                component="pre"
                sx={{
                    m: 0, mt: 0.5, p: 1.5,
                    background: "#f6f7f9",
                    border: "1px solid rgba(170,170,170,0.3)",
                    borderRadius: "8px",
                    fontSize: 12,
                    lineHeight: 1.6,
                    maxHeight: 280,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
            >
                {text || "— none —"}
            </Box>
        </Box>
    );
};

/**
 * Full detail for a single logged API call. Fetched on demand rather than being
 * carried in the list payload, so the table request stays free of JSON blobs.
 */
const ApiLogDetailDrawer = ({ logId, open, onClose }) => {
    const { data, isFetching, isError } = useGetApiLogDetailQuery(logId, { skip: !logId || !open });
    const log = data?.result;

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: { xs: "100vw", sm: 520 }, p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>API Call Detail</Typography>
                    <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </Stack>

                {isFetching && (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={28} /></Stack>
                )}

                {!isFetching && isError && (
                    <Alert severity="error">Could not load this log entry.</Alert>
                )}

                {!isFetching && !isError && !log && (
                    <Alert severity="info">This log entry no longer exists — it may have been purged by the retention job.</Alert>
                )}

                {!isFetching && log && (
                    <Stack gap={2}>
                        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                            <StatusChip code={log.statusCode} />
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                                {routeLabel(log.method, log.routePath)}
                            </Typography>
                        </Stack>

                        <Divider />

                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                            <Field label="Time" value={formatDateTime(log.createdDate)} />
                            <Field label="Duration" value={formatMs(log.durationMs)} />
                            <Field label="User" value={log.userId ? `#${log.userId}` : "Anonymous"} />
                            <Field label="User type" value={log.userType} />
                            <Field label="Side" value={log.side} />
                            <Field label="IP address" value={log.ipAddress} mono />
                        </Box>

                        <Field label="Full URL" value={log.url} mono />

                        <Divider />

                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                            <Field label="Device" value={describeClient(log.userAgent)} />
                            <Field label="Device ID" value={log.deviceId} mono />
                            <Field label="Session ID" value={log.sessionId} mono />
                            <Field label="Browser session" value={log.browserSessionId} mono />
                        </Box>
                        <Field label="User agent" value={log.userAgent} mono />
                        <Field label="Token hash" value={log.tokenHash} mono />

                        <Divider />

                        <JsonBlock title="Request body" value={log.requestBody} />
                        <JsonBlock title="Query params" value={log.queryParams} />

                        <Alert severity="info" sx={{ mt: 1 }}>
                            Credentials, OTPs, card and identity numbers are replaced with
                            <strong> ***REDACTED*** </strong> before the log is written, and bodies over
                            8000 characters are truncated. Response bodies are never stored.
                        </Alert>
                    </Stack>
                )}
            </Box>
        </Drawer>
    );
};

export default ApiLogDetailDrawer;
